import {
  Button,
  cn,
  FileTrigger,
  Form,
  GradientText,
  Icon,
  IconButton,
  ModalDialog,
  ModalTrigger,
  Text,
  TextArea,
  useMediaQuery,
} from "@archery-inc/design-system";
import { amplitude } from "../api/third-party";
import { AnimatePresence, motion } from "framer-motion";
import {
  base64ToUrl,
  getGenerationPreview,
  useCategories,
  useCreateGenerationPreview as useCreateGenerationPreviewImg2Img,
  useCreateImageToVideoImmediate,
  useCreatePreviewFromPrompt,
  useCreatePreviewFromText,
  useGetPromptPresetsSuggestions,
  useGetPromptPresetsSuggestionsFromText,
  useStartGeneration,
  useUploadBase64Encoded,
} from "../api/graphql/ai";
import {
  AiAnimatedElementPreset,
  AiAnimatedElementsGeneration,
  AiRevealAspectRatio,
  BucketInfoInput,
  CreateAiAnimatedElementsWithPresetsPreviewMutation,
  CreateAnimatedVideoFromPromptMutation,
  CreateAnimatedVideoFromTextMutation,
  Exact,
  GenerateAiAnimatedPictureInputFromBase64Query,
  InputMaybe,
  IntermediateFramesValidationInput,
  PresetConfig,
  PresetFlow,
  RevealsCategoryWithPresets,
  Scalars,
} from "../__generated__/graphql";
import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { create, StateCreator } from "zustand";
import {
  compressImage,
  FileInfo,
  getBase64FromFile,
  resizeAndCompressImageToWebP,
} from "../common/utils/image";
import { getFileUrl, uploadFile, useFileUrl } from "../api/firebaseStorage";
import {
  FetchResult,
  LazyQueryExecFunction,
  MutationFunctionOptions,
} from "@apollo/client";
import { auth } from "../api/firebaseAuth";
import { createUUID } from "../common/utils/uuid";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "@tanstack/react-router";
import { presetPromptObject } from "../common/utils/preset";

const defaultState = {
  activePreset: null,
  fileInfo: null,
  presetData: null,
  step: "preset" as const,
  generationId: null,
  object: null,
  text: "",
};

type CreateGenerationFromPrompt = (
  options?: MutationFunctionOptions<
    CreateAnimatedVideoFromPromptMutation,
    Exact<{ preset: PresetConfig }>
  >,
) => Promise<FetchResult<CreateAnimatedVideoFromPromptMutation>>;

type CreateGeneration = (
  options?: MutationFunctionOptions<
    CreateAiAnimatedElementsWithPresetsPreviewMutation,
    Exact<{
      inputFileUrl: Scalars["String"]["input"];
      presets?: InputMaybe<PresetConfig[] | PresetConfig>;
      inputBucketInfo?: InputMaybe<BucketInfoInput>;
      needsIntermediateFramesValidation?: InputMaybe<IntermediateFramesValidationInput>;
    }>
  >,
) => Promise<FetchResult<CreateAiAnimatedElementsWithPresetsPreviewMutation>>;

type CreateGenerationFromText = (
  options?: MutationFunctionOptions<
    CreateAnimatedVideoFromTextMutation,
    Exact<{
      textInput: Scalars["String"]["input"];
      preset: PresetConfig;
    }>
  >,
) => Promise<FetchResult<CreateAnimatedVideoFromTextMutation>>;

interface AiStore {
  activePreset: AiAnimatedElementPreset | null;
  presetData: { content: string } | null;
  fileInfo: FileInfo | null;
  generationId: string | null;
  step: "preset" | "preview";
  text: string;
  object: string | null;
  goBack: () => void;
  setText: (text: string) => void;
  setPresetData: (data: { content: string }) => void;
  setActivePreset: (preset: AiAnimatedElementPreset | null) => void;
  replaceImage: (
    file: File,
    removeBackgroundMutation?: LazyQueryExecFunction<
      GenerateAiAnimatedPictureInputFromBase64Query,
      Exact<{
        base64Data: Scalars["String"]["input"];
        fileName: Scalars["String"]["input"];
      }>
    >,
  ) => Promise<FileInfo>;
  removeImage: () => void;
  reset: () => void;
  createPreview: () => void;
  setObject: (object: string) => void;
  getGenerationPreviewFromPrompt: (
    mutate: CreateGenerationFromPrompt,
    presetId: string,
    presetData?: { content: string },
  ) => Promise<{ url?: string; generationId: string }>;
  getGenerationPreviewFromText: (
    mutate: CreateGenerationFromText,
    presetId: string,
    presetData?: { content: string },
  ) => Promise<{ url?: string; generationId: string }>;
  getGenerationPreviewImg2Img: (
    mutate: CreateGeneration,
    presetId: string,
    presetData?: { content: string },
  ) => Promise<{ url?: string; generationId: string }>;
}

const createAiStore: StateCreator<AiStore> = (set, get) => ({
  ...defaultState,
  setObject: (object) => set({ object }),
  setActivePreset: (preset) => {
    if (preset) {
      amplitude.logEvent("Web:Client:MojoAI:SelectPreset", {
        presetId: preset.presetId,
      });
      set({ activePreset: preset, step: "preset", presetData: null });
    } else set(defaultState);
  },
  reset() {
    set(defaultState);
  },
  goBack: () => {
    set({ step: "preset" });
  },
  setPresetData: (presetData) => {
    set({ presetData });
  },
  setText(text) {
    set({ text: text.toUpperCase() });
  },
  async replaceImage(file, removeBackgroundMutation) {
    amplitude.logEvent("Web:Client:MojoAI:UploadImage", {
      presetId: get().activePreset?.presetId,
    });
    if (removeBackgroundMutation) {
      const originalFileInfo = await compressImage(file);
      set({ fileInfo: { base64: originalFileInfo.base64, filePath: "" } });
      const { data } = await removeBackgroundMutation({
        variables: {
          base64Data: originalFileInfo.base64!,
          fileName: originalFileInfo.filePath,
        },
      });
      const gcs = data!.generateAiAnimatedPictureInputFromBase64;
      const fileInfo = {
        bucketName: gcs.bucketName,
        filePath: gcs.filePath,
        base64: originalFileInfo.base64,
      };
      set({ fileInfo });
      return fileInfo;
    }

    const compressedFile = await resizeAndCompressImageToWebP(file);
    const base64 = await getBase64FromFile(compressedFile);
    set({ fileInfo: { base64, filePath: "" } });
    const fileName = `${await createUUID()}.webp`;
    const gcs = {
      file: compressedFile,
      bucketName: import.meta.env.VITE_TEMPORARY_FILES_BUCKET_NAME,
      uploadPath: auth.currentUser!.uid + "/ai-reveals/input-files/" + fileName,
    };

    await uploadFile(gcs, { appendFileName: false });
    const fileInfo = {
      bucketName: gcs.bucketName,
      filePath: gcs.uploadPath,
      base64,
    };
    set({ fileInfo });

    return fileInfo;
  },
  removeImage() {
    amplitude.logEvent("Web:Client:MojoAI:RemoveImage", {
      presetId: get().activePreset?.presetId,
    });
    set({ fileInfo: null });
  },
  createPreview() {
    amplitude.logEvent("Web:Client:MojoAI:CreatePreview", {
      presetId: get().activePreset?.presetId,
    });
    set({ step: "preview" });
  },
  async getGenerationPreviewFromPrompt(mutate, presetId, presetData) {
    const { presetFlow } = get().activePreset!;
    const { data } = await mutate({
      variables: {
        preset: {
          presetId: presetData ? "custom" : presetId,
          customPrompt: presetData?.content
            .replace(/"/g, "'")
            .replace(/\n/g, " "),
          modelName: "runway",
          presetFlow,
          aspectRatio: AiRevealAspectRatio.RunwayPortraitV4,
          seeds: [Math.floor(Math.random() * 1000000)],
        },
      },
    });
    return getGenerationPreview(
      data?.createAnimatedVideoFromPromptAnonymous as AiAnimatedElementsGeneration,
    );
  },
  async getGenerationPreviewFromText(mutate, presetId, presetData) {
    const { presetFlow } = get().activePreset!;
    const { data } = await mutate({
      variables: {
        textInput: get().text,
        preset: {
          presetId: presetData ? "custom" : presetId,
          customPrompt: presetData?.content
            .replace(/"/g, "'")
            .replace(/\n/g, " "),
          modelName: "runway",
          presetFlow,
          aspectRatio: AiRevealAspectRatio.RunwayPortraitV4,
          seeds: [Math.floor(Math.random() * 1000000)],
        },
      },
    });
    return getGenerationPreview(
      data?.createAnimatedVideoFromTextAnonymous as AiAnimatedElementsGeneration,
    );
  },
  async getGenerationPreviewImg2Img(mutate, presetId, presetData) {
    const fileInfo = get().fileInfo;
    const { presetFlow } = get().activePreset!;
    const url = (await getFileUrl(fileInfo!.bucketName!, fileInfo!.filePath))!;
    const { data } = await mutate({
      variables: {
        inputFileUrl: url,
        inputBucketInfo: {
          bucketName: fileInfo!.bucketName!,
          filePath: fileInfo!.filePath,
        },
        needsIntermediateFramesValidation: { numberOfVariations: 1 },
        presets: [
          {
            presetId: presetData ? "custom" : presetId,
            customPrompt: presetData?.content
              .replace(/"/g, "'")
              .replace(/\n/g, " "),
            presetFlow,
            modelName: "runway",
            aspectRatio: AiRevealAspectRatio.RunwayPortraitV4,
            seeds: [Math.floor(Math.random() * 1000000)],
          },
        ],
      },
    });
    return getGenerationPreview(
      data?.createAiAnimatedElementsWithPresetsAnonymous as AiAnimatedElementsGeneration,
    );
  },
});

// Selectors are called directly (useStore((s) => s.x)) rather than through a
// generated store.use.x() map: the React Compiler cannot recognize those
// wrappers as hooks and memoizes them away, which breaks the Rules of Hooks
// at runtime in compiled builds.
const useStore = create(createAiStore);

export default function CreateAiPage() {
  const { data } = useCategories();

  useEffect(() => {
    amplitude.logEvent("Web:Client:MojoAI:View");
  }, []);

  const categories = useMemo(() => {
    if (!data) return [];

    const first = data.getRevealsCategoriesWithPresets.categories.find(
      (c) => c.categoryId === "eshop-listings",
    );

    const rest = data.getRevealsCategoriesWithPresets.categories
      .map((c) => ({
        ...c,
        presets: c.presets.filter(
          (p) => p.presetFlow !== PresetFlow.ImageAndTextToVideo,
        ),
      }))
      .filter((c) => c.presets.length > 0 && c.categoryId !== "eshop-listings");

    if (!first) return rest;
    return [first, ...rest];
  }, [data]);

  if (!data) {
    return <CategoriesSkeleton />;
  }

  return <Categories categories={categories as RevealsCategoryWithPresets[]} />;
}

function Categories({
  categories,
}: {
  categories: RevealsCategoryWithPresets[];
}) {
  const activePreset = useStore((s) => s.activePreset);
  const setActive = useStore((s) => s.setActivePreset);
  const step = useStore((s) => s.step);
  const goBack = useStore((s) => s.goBack);
  const activeCategory = categories.find((category) =>
    activePreset?.categoryIds?.includes(category.categoryId),
  );

  return (
    <div className="pb-10">
      {categories.map((category) => {
        return (
          <div key={category.categoryId} className="mb-10">
            <h2 className="text-2xl font-medium">{category.label}</h2>
            <p className="mb-2 text-grey-5">{category.description}</p>
            <Category key={category.categoryId} category={category} />
          </div>
        );
      })}
      <ModalTrigger
        isOpen={!!activePreset}
        onOpenChange={(closed) => {
          if (!closed) {
            amplitude.logEvent("Web:Client:MojoAI:ClosePresetModal", {
              presetId: activePreset?.presetId,
            });
            setActive(null);
          }
        }}
      >
        <ModalDialog
          containerClassName="h-screen relative rounded-1 max-w-full"
          className="rounded-1"
          transition={layoutTransition}
          layoutId={
            "background-" + activeCategory?.categoryId + activePreset?.presetId
          }
        >
          {({ close }) => (
            <>
              <div className="absolute p-6 z-20 w-full flex justify-between">
                <div className="flex gap-2">
                  <IconButton icon="close" color="dark" onPress={close} />
                  {step === "preview" && (
                    <IconButton
                      icon="arrow_full_left"
                      color="grey"
                      onPress={goBack}
                    />
                  )}
                </div>
              </div>
              {step === "preset" && <ActivePreset category={activeCategory} />}
              {step === "preview" && <Preview />}
            </>
          )}
        </ModalDialog>
      </ModalTrigger>
    </div>
  );
}

function Preview() {
  const [preview, setPreview] = useState<{
    url?: string;
    generationId: string;
  } | null>(null);
  const activePreset = useStore((s) => s.activePreset);

  const getPreviewPrompt2Img = useStore((s) => s.getGenerationPreviewFromPrompt);
  const [createPreviewPrompt2Img] = useCreatePreviewFromPrompt();

  const getPreviewImg2Img = useStore((s) => s.getGenerationPreviewImg2Img);
  const [createPreviewImg2Img] = useCreateGenerationPreviewImg2Img();

  const getPreviewFromText = useStore((s) => s.getGenerationPreviewFromText);
  const [createPreviewFromText] = useCreatePreviewFromText();

  const [startMutation, { loading }] = useStartGeneration();
  const reset = useStore((s) => s.reset);

  const navigate = useNavigate();
  async function start() {
    await startMutation({
      variables: {
        generationId: preview!.generationId,
        previewUrl: preview!.url!,
      },
    });
    amplitude.logEvent("Web:Client:MojoAI:StartGeneration", {
      generationId: preview!.generationId,
    });
    void navigate({ to: "/my-generations" });
    reset();
  }

  const presetData = useStore((s) => s.presetData);

  const generate = () => {
    setPreview(null);
    if (activePreset?.presetFlow === PresetFlow.TextToVideo) {
      void getPreviewFromText(
        createPreviewFromText,
        activePreset.presetId,
        presetData ?? undefined,
      ).then(setPreview);
      return;
    }
    if (activePreset?.presetFlow === PresetFlow.PromptToVideo) {
      void getPreviewPrompt2Img(
        createPreviewPrompt2Img,
        activePreset.presetId,
        presetData ?? undefined,
      ).then(setPreview);
      return;
    }
    void getPreviewImg2Img(
      createPreviewImg2Img,
      activePreset!.presetId,
      presetData ?? undefined,
    ).then(setPreview);
  };

  useEffect(() => {
    generate();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- generate is redefined each render; the effect should only follow presetData
  }, [presetData]);

  return (
    <div className="flex flex-col justify-between p-16 sm:px-16 px-3 h-screen w-screen max-w-lg">
      <h2 className="sm:my-3 my-0 text-center sm:text-4xl text-2xl font-medium mb-3">
        <FormattedMessage id="ai_reveals.preview_generating" />
      </h2>
      <div className="mb-8 relative rounded-2 overflow-hidden">
        {!preview ? (
          <div className="relative overflow-hidden rounded-2">
            <Skeleton className="w-full" style={{ aspectRatio: "9 / 16" }} />
            <img
              className="z-20 opacity-30 blur-0 absolute top-0 left-1/2 -translate-x-1/2 h-full"
              src={activePreset?.thumbnailData?.thumbnailUrl ?? undefined}
            />
          </div>
        ) : (
          <img src={preview.url} className="w-full" />
        )}
        {!preview ? (
          <div className="z-30 absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center">
            <div className="mb-2 flex items-center text-sm bg-white rounded-2 px-2 py-1">
              <Icon icon="spinner" className="mr-2" />
              <FormattedMessage id="ai_reveals.preview_generating" />
            </div>
            <div
              className="text-white sm:text-base text-xs whitespace-nowrap"
              style={{ textShadow: "0px 0px 4px #000000" }}
            >
              <FormattedMessage id="ai_reveals.preview_generating_waiting_time" />
            </div>
          </div>
        ) : (
          <Button
            onPress={generate}
            size="xs"
            color="dark-grey"
            leftIcon="shuffle"
            className="z-30 absolute bottom-3 left-1/2 -translate-x-1/2"
          >
            <FormattedMessage id="ai_reveals.regenerate" />
          </Button>
        )}
      </div>
      <div className="flex justify-center">
        <Button
          size="l"
          color="gradient"
          leftIcon={loading ? "spinner" : "sparkle"}
          isDisabled={!preview || loading}
          onPress={start}
        >
          <FormattedMessage id="ai_reveals.generate_video_cta" />
        </Button>
      </div>
    </div>
  );
}

function Category({ category }: { category: RevealsCategoryWithPresets }) {
  const parentRef = useRef(null);
  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: category.presets.length,
    getScrollElement: () => parentRef.current,
    gap: 8,
    estimateSize: () => 250,
  });

  return (
    <div
      ref={parentRef}
      key={category.categoryId}
      className="w-full overflow-x-auto overflow-y-hidden"
      style={{ height: (250 * 16) / 9 }}
    >
      <ul
        className="flex relative h-full"
        style={{ width: `${columnVirtualizer.getTotalSize()}px` }}
      >
        {columnVirtualizer.getVirtualItems()?.map((virtualItem) => {
          return (
            <Preset
              category={category}
              virtualItem={virtualItem}
              key={virtualItem.index}
            />
          );
        })}
      </ul>
    </div>
  );
}

function Preset({
  category,
  virtualItem,
}: {
  category: RevealsCategoryWithPresets;
  virtualItem: { index: number; size: number; start: number };
}) {
  const setActivePreset = useStore((s) => s.setActivePreset);
  const item = category.presets[virtualItem.index];
  return (
    <li
      key={item.presetId}
      className="shrink-0 absolute bottom-0 left-0 pr-1 cursor-pointer"
      onClick={() => setActivePreset(item)}
      style={{
        width: `${virtualItem.size}px`,
        transform: `translateX(${virtualItem.start}px)`,
      }}
    >
      <motion.div
        layoutId={"background-" + category.categoryId + item.presetId}
        transition={layoutTransition}
      >
        <motion.video
          layoutId={"video-" + category.categoryId + item.presetId}
          preload="auto"
          transition={layoutTransition}
          src={item.previewVideoForCategory?.url ?? item.previewVideoUrl}
          muted
          loop
          autoPlay
          className="w-full object-cover rounded-2 bg-grey-4"
        />
      </motion.div>
    </li>
  );
}

const layoutTransition = {
  type: "spring",
  stiffness: 200,
  mass: 0.3,
  damping: 20,
};

function ActivePreset({ category }: { category?: RevealsCategoryWithPresets }) {
  const activePreset = useStore((s) => s.activePreset);
  const fileInfo = useStore((s) => s.fileInfo);
  const object = useStore((s) => s.object);
  const text = useStore((s) => s.text);
  const isMobile = useMediaQuery("sm");

  return (
    <div
      className="relative max-w-full h-screen min-w-[500px]"
      style={{ aspectRatio: "9 / 16" }}
    >
      <motion.video
        layoutId={
          "video-" + (category?.categoryId ?? "") + activePreset?.presetId
        }
        transition={layoutTransition}
        preload="auto"
        className="w-full h-full absolute object-cover"
        autoPlay
        muted
        loop
        src={
          activePreset?.previewVideoForCategory?.url ??
          activePreset?.previewVideoUrl
        }
      />
      <div className="absolute bottom-0 left-0 w-full py-5">
        {fileInfo && (
          <div className="px-2 flex justify-between items-center mb-2 whitespace-nowrap">
            <span
              className="text-white sm:text-base text-sm"
              style={{ textShadow: "0px 0px 4px #000000" }}
            >
              {activePreset?.presetFlow === PresetFlow.ImageToVideo &&
                object && (
                  <FormattedMessage
                    id="ai_reveal_custom_preset_suggestion"
                    values={{
                      object: (
                        <GradientText style={{ textShadow: "initial" }}>
                          {object}
                        </GradientText>
                      ),
                    }}
                  />
                )}
              {activePreset?.presetFlow === PresetFlow.TextToVideo && text && (
                <FormattedMessage
                  id="ai_reveal_custom_preset_suggestion"
                  values={{
                    object: (
                      <GradientText style={{ textShadow: "initial" }}>
                        {text}
                      </GradientText>
                    ),
                  }}
                />
              )}
            </span>
            <ModalTrigger>
              {isMobile ? (
                <IconButton icon="edit" color="black" className="scale-75" />
              ) : (
                <Button
                  leftIcon="edit"
                  size="xs"
                  className="scale-75"
                  color="dark-grey"
                >
                  <FormattedMessage
                    id="ai_reveals_edit_preset_title"
                    values={{
                      preset: <FormattedMessage id="ai_reveal_preset" />,
                    }}
                  />
                </Button>
              )}
              <PresetEditDialog />
            </ModalTrigger>
          </div>
        )}
        <SuggestedPresetBar category={category} />
        {activePreset?.presetFlow === PresetFlow.ImageToVideo && (
          <Img2VideoPreviewBar />
        )}
        {activePreset?.presetFlow === PresetFlow.ImageToVideoImmediate && (
          <Img2VideoImmediatePreviewBar />
        )}
        {activePreset?.presetFlow === PresetFlow.ImageStyleTransferToVideo && (
          <ImgStyleTransferPreviewBar />
        )}
        {activePreset?.presetFlow === PresetFlow.TextToVideo && (
          <Text2VideoPreviewBar />
        )}
        {activePreset?.presetFlow === PresetFlow.PromptToVideo && (
          <Prompt2VideoPreviewBar preset={activePreset} />
        )}
      </div>
    </div>
  );
}

function SuggestedPresetBar({
  category,
}: {
  category?: RevealsCategoryWithPresets;
}) {
  const fileInfo = useStore((s) => s.fileInfo);
  const activePreset = useStore((s) => s.activePreset);

  if (!fileInfo?.base64) {
    return <SuggestedPresetList presets={category?.presets ?? []} />;
  }
  if (activePreset?.presetFlow === PresetFlow.ImageToVideo) {
    return <Img2VideoSuggestedPresets />;
  }
  if (activePreset?.presetFlow === PresetFlow.TextToVideo) {
    return <Text2VideoSuggestedPresets />;
  }
  return <SuggestedPresetList presets={category?.presets ?? []} />;
}

function Text2VideoSuggestedPresets() {
  const text = useStore((s) => s.text);
  const { data, loading } = useGetPromptPresetsSuggestionsFromText(text);
  return (
    <SuggestedPresetList
      loading={loading}
      presets={
        (data?.getPresetSuggestionsFromText
          .suggestions as AiAnimatedElementPreset[]) ?? []
      }
    />
  );
}

function Img2VideoSuggestedPresets() {
  const fileInfo = useStore((s) => s.fileInfo);
  const { data, loading } = useGetPromptPresetsSuggestions(
    "data:image/png;base64," + fileInfo!.base64!,
  );
  const setObject = useStore((s) => s.setObject);
  if (data) {
    setObject(data.getPresetSuggestionsFromImage.object);
  }
  return (
    <SuggestedPresetList
      loading={loading}
      presets={
        (data?.getPresetSuggestionsFromImage
          .suggestions as AiAnimatedElementPreset[]) ?? []
      }
    />
  );
}

function SuggestedPresetList({
  presets,
  loading = false,
}: {
  presets: AiAnimatedElementPreset[];
  loading?: boolean;
}) {
  const activePreset = useStore((s) => s.activePreset);
  const setActivePreset = useStore((s) => s.setActivePreset);
  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, y: 30 }}
      animate={{
        opacity: 1,
        y: 0,
        transition: { delay: 0.3, type: "ease-in" },
      }}
      className="flex gap-4 px-3 w-full overflow-x-auto overflow-y-visible no-scroll mb-4"
    >
      {loading
        ? Array.from({ length: 15 }).map((_, i) => (
            <div
              key={i}
              className="size-12 shrink-0 overflow-hidden gap-1 rounded-1 cursor-pointer hover:scale-110 transition-all"
            >
              <div className="w-full h-full bg-grey-4 animate-pulse"></div>
            </div>
          ))
        : presets.map((preset) => (
            <div
              key={preset.presetId}
              className={cn(
                "size-12 shrink-0 overflow-hidden rounded-1 cursor-pointer hover:scale-110 transition-all",
                {
                  "border-4 border-tangerine-1":
                    preset.presetId === activePreset?.presetId,
                },
              )}
              onClick={() => setActivePreset(preset)}
            >
              <video
                key={preset.presetId}
                src={
                  preset.previewVideoForCategory?.url ?? preset.previewVideoUrl
                }
                muted
                loop
                autoPlay
              />
            </div>
          ))}
    </motion.div>
  );
}

function Prompt2VideoPreviewBar({
  preset,
}: {
  preset: AiAnimatedElementPreset;
}) {
  const createPreview = useStore((s) => s.createPreview);
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <div className="flex flex-col justify-center">
        <div className="flex flex-col bg-grey-4/50 backdrop-blur-md mx-2 rounded-2 py-2 mb-2 relative p-3">
          <div className="flex justify-center overflow-hidden">
            <Button
              leftIcon="text_style"
              color="black"
              className="mb-2"
              onPress={() => setIsOpen(true)}
            >
              <FormattedMessage id="ai_reveals.edit_text_title" />
            </Button>
          </div>
          <GradientText className="text-5xl overflow-hidden">
            {presetPromptObject(preset.modelData.jsonPromptObject)
              ?.background_image_prompt}
          </GradientText>
        </div>
        <Button color="gradient" size="l" onPress={createPreview}>
          <FormattedMessage id="ai_reveals.generate_preview_cta" />
        </Button>
      </div>
      <ModalTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <PresetEditDialog />
      </ModalTrigger>
    </>
  );
}

function Text2VideoPreviewBar() {
  const text = useStore((s) => s.text);
  const createPreview = useStore((s) => s.createPreview);
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      {text ? (
        <div className="flex flex-col justify-center">
          <div className="flex flex-col justify-center items-center bg-grey-4/50 backdrop-blur-md mx-2 rounded-2 py-2 mb-2 relative">
            <Button
              leftIcon="text_style"
              color="black"
              className="mb-2"
              onPress={() => setIsOpen(true)}
            >
              <FormattedMessage id="ai_reveals.edit_text_title" />
            </Button>
            <GradientText className="text-5xl">{text}</GradientText>
          </div>
          <Button color="gradient" size="l" onPress={createPreview}>
            <FormattedMessage id="ai_reveals.generate_preview_cta" />
          </Button>
        </div>
      ) : (
        <div className="flex justify-center">
          <Button
            color="gradient"
            size="l"
            leftIcon="plus"
            onPress={() => setIsOpen(true)}
          >
            <FormattedMessage id="ai_reveals.add_text" />
          </Button>
        </div>
      )}
      <ModalTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
        <ModalDialog className="relative">
          {({ close }) => <Text2VideoPreviewBarForm close={close} />}
        </ModalDialog>
      </ModalTrigger>
    </>
  );
}

function Text2VideoPreviewBarForm({ close }: { close: () => void }) {
  const [text, setText] = useState("");
  const setGlobalText = useStore((s) => s.setText);
  const maxLength = 30;
  return (
    <>
      <IconButton
        icon="close"
        color="grey"
        className="absolute top-2 right-2"
        onPress={close}
      />
      <div className="p-5">
        <h1 className="font-medium text-center text-2xl mb-4">
          <FormattedMessage id="ai_reveals.text_edit_view_title" />
        </h1>
        <div className="relative">
          <textarea
            maxLength={maxLength}
            value={text}
            rows={6}
            ref={(ref) => {
              ref?.focus();
            }}
            onChange={(e) => setText(e.target.value.toUpperCase())}
            className="border-1 border-grey-6 focus:border-2 outline-hidden focus:border-tangerine p-3 rounded-3 text-5xl"
          />
          <span className="absolute bottom-4 left-3 text-grey-7 text-sm">
            ({text.length}/{maxLength})
          </span>
        </div>
        <div className="flex justify-center">
          <Button
            color="gradient"
            size="l"
            onPress={() => {
              setGlobalText(text);
              close();
            }}
          >
            <FormattedMessage id="common_done" />
          </Button>
        </div>
      </div>
    </>
  );
}

function ImgStyleTransferPreviewBar() {
  const replaceImage = useStore((s) => s.replaceImage);
  const createPreview = useStore((s) => s.createPreview);
  const fileInfo = useStore((s) => s.fileInfo);

  const [isLoading, setIsLoading] = useState(false);
  const onReplace = async (files: FileList | null) => {
    setIsLoading(true);
    if (files) {
      await replaceImage(files[0]);
    }
    setIsLoading(false);
  };

  return (
    <>
      <AnimatePresence>
        {fileInfo && (
          <Composition
            loaderLabel={
              <FormattedMessage id="account_uploading_in_progress" />
            }
            onReplace={onReplace}
          />
        )}
      </AnimatePresence>
      <div className="flex justify-center">
        {fileInfo?.filePath ? (
          <Button
            color="gradient"
            size="l"
            onPress={createPreview}
            isDisabled={isLoading}
          >
            <FormattedMessage id="ai_reveals.generate_preview_cta" />
          </Button>
        ) : (
          <FileTrigger
            acceptedFileTypes={["image/*"]}
            allowsMultiple={false}
            onSelect={onReplace}
          >
            <Button
              isDisabled={isLoading}
              color="gradient"
              size="l"
              leftIcon="photo"
            >
              <FormattedMessage id="ai_reveals.select_photo" />
            </Button>
          </FileTrigger>
        )}
      </div>
    </>
  );
}

function Img2VideoImmediatePreviewBar() {
  const replaceImage = useStore((s) => s.replaceImage);
  const activePreset = useStore((s) => s.activePreset);
  const [mutate] = useCreateImageToVideoImmediate();
  const navigate = useNavigate();
  const reset = useStore((s) => s.reset);

  const [isLoading, setIsLoading] = useState(false);
  const onReplace = async (files: FileList | null) => {
    setIsLoading(true);
    if (files) {
      const fileInfo = await replaceImage(files[0]);
      const imageUrl = await getFileUrl(
        fileInfo.bucketName!,
        fileInfo.filePath,
      );

      await mutate({
        variables: {
          imageUrl: imageUrl!,
          inputBucketInfo: {
            bucketName: fileInfo.bucketName!,
            filePath: fileInfo.filePath,
          },
          preset: {
            presetId: activePreset!.presetId,
            aspectRatio: AiRevealAspectRatio.RunwayPortraitV4,
          },
        },
      });

      void navigate({ to: "/my-generations" });
      reset();
    }
    setIsLoading(false);
  };

  return (
    <div className="flex justify-center">
      <FileTrigger
        acceptedFileTypes={["image/*"]}
        allowsMultiple={false}
        onSelect={onReplace}
      >
        <Button
          isDisabled={isLoading}
          color="gradient"
          size="l"
          leftIcon={isLoading ? "spinner" : "photo"}
        >
          <FormattedMessage id="ai_reveals.select_photo" />
        </Button>
      </FileTrigger>
    </div>
  );
}

function Img2VideoPreviewBar() {
  const replaceImage = useStore((s) => s.replaceImage);
  const createPreview = useStore((s) => s.createPreview);
  const fileInfo = useStore((s) => s.fileInfo);
  const [removeBackgroundMutation] = useUploadBase64Encoded();

  const [isLoading, setIsLoading] = useState(false);
  const onReplace = async (files: FileList | null) => {
    setIsLoading(true);
    if (files) {
      await replaceImage(files[0], removeBackgroundMutation);
    }
    setIsLoading(false);
  };

  return (
    <>
      <AnimatePresence>
        {fileInfo && (
          <Composition
            loaderLabel={
              <FormattedMessage id="creation_mediaControls_removing_background" />
            }
            onReplace={onReplace}
          />
        )}
      </AnimatePresence>
      <div className="flex justify-center">
        {fileInfo?.filePath ? (
          <Button
            color="gradient"
            size="l"
            isDisabled={isLoading}
            onPress={createPreview}
          >
            <FormattedMessage id="ai_reveals.generate_preview_cta" />
          </Button>
        ) : (
          <FileTrigger
            acceptedFileTypes={["image/*"]}
            allowsMultiple={false}
            onSelect={onReplace}
          >
            <Button
              isDisabled={isLoading}
              color="gradient"
              size="l"
              leftIcon="photo"
            >
              <FormattedMessage id="ai_reveals.select_photo" />
            </Button>
          </FileTrigger>
        )}
      </div>
    </>
  );
}

function Composition({
  loaderLabel,
  onReplace,
}: {
  loaderLabel: React.ReactNode;
  onReplace: (files: FileList | null) => Promise<void>;
}) {
  const fileInfo = useStore((s) => s.fileInfo);
  const src = useFileUrl(fileInfo?.bucketName, fileInfo?.filePath);
  const removeImage = useStore((s) => s.removeImage);
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={layoutTransition}
      className="flex justify-center bg-grey-4/50 backdrop-blur-md mx-2 rounded-2 py-2 mb-2 relative"
    >
      <FileTrigger
        acceptedFileTypes={["image/*"]}
        allowsMultiple={false}
        onSelect={onReplace}
      >
        <Button
          leftIcon="replace"
          size="xs"
          color="dark-grey"
          className="scale-75 absolute top-2 left-2"
        >
          <FormattedMessage id="image_picker_replace" />
        </Button>
      </FileTrigger>
      <div className="relative">
        <img
          className={cn("h-36 rounded-2", { "blur-sm": !src })}
          src={src ?? base64ToUrl(fileInfo?.base64) ?? ""}
        />
        {!src && (
          <div className="text-center whitespace-nowrap flex items-center text-4xs z-30 absolute bottom-2 left-1/2 -translate-x-1/2 px-2  py-1 rounded-2 bg-white">
            <Icon icon="spinner" className="size-3 mr-2" />
            {loaderLabel}
          </div>
        )}
      </div>
      <Button
        rightIcon="close"
        size="xs"
        color="dark-grey"
        className="scale-75 absolute top-2 right-2"
        onPress={removeImage}
      >
        <FormattedMessage id="image_picker_remove" />
      </Button>
    </motion.div>
  );
}

function CategoriesSkeleton() {
  const style = { height: (250 * 16) / 9 };
  return (
    <div>
      <div className="mb-10">
        <Skeleton className="w-48 h-8 mb-3" />
        <div className="flex gap-3 w-full overflow-hidden">
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
        </div>
      </div>
      <div>
        <Skeleton className="w-48 h-8 mb-3" />
        <div className="flex gap-3 w-full overflow-hidden">
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
          <Skeleton className="w-[250px]" style={style} />
        </div>
      </div>
    </div>
  );
}

function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={cn(
        "relative bg-grey-2 rounded-2 flex-nowrap shrink-0 overflow-hidden",
        className,
      )}
      style={style}
    >
      <motion.div
        className="absolute left-0 top-0 h-full w-16"
        initial={{
          skewX: 35,
          background:
            "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
        }}
        transition={{
          repeat: Infinity,
          duration: 1,
          repeatDelay: 1,
        }}
        animate={{
          left: ["-100%", "150%"],
        }}
      />
    </div>
  );
}

function PresetEditDialog() {
  const intl = useIntl();
  const activePreset = useStore((s) => s.activePreset);
  const prompts = presetPromptObject(activePreset?.modelData.jsonPromptObject);
  const initialValue = `${prompts?.background_image_prompt}\n\n${prompts?.input_prompt}`;
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initialValue is derived from activePreset, which is already a dependency
  }, [activePreset]);

  const createPreview = useStore((s) => s.createPreview);
  const setPresetData = useStore((s) => s.setPresetData);
  const onGenerate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = Object.fromEntries(new FormData(e.currentTarget));
    setPresetData(formData as { content: string });
    createPreview();
  };

  return (
    <ModalDialog
      className="flex flex-col gap-3 p-10 relative"
      containerClassName="w-full max-w-2xl"
    >
      {({ close }) => (
        <Form onSubmit={onGenerate}>
          <IconButton
            onPress={close}
            icon="close"
            color="grey"
            className="absolute top-4 right-4"
          />
          <Text variant="title-1" weight="medium">
            <FormattedMessage
              id="ai_reveals_edit_preset_title"
              values={{
                preset: (
                  <GradientText>
                    <FormattedMessage id="ai_reveal_preset" />
                  </GradientText>
                ),
              }}
            />
          </Text>
          <div>
            <Text
              elementType="label"
              className="text-grey-10 inline-block"
              // @ts-expect-error (this prop is specific to label)
              htmlFor="content"
            >
              <AnimatePresence>
                {value !== "" && (
                  <motion.span
                    className="inline-block"
                    exit={{ y: 5, opacity: 0 }}
                    initial={{ y: 5, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                  >
                    {intl.formatMessage({
                      id: "ai_reveal_custom_preset_label",
                    })}
                  </motion.span>
                )}
              </AnimatePresence>
            </Text>
            <div className="border-2 rounded-2 border-tangerine">
              <TextArea
                autoFocus
                placeholder={intl.formatMessage({
                  id: "ai_reveal_custom_preset_label",
                })}
                autoHeight
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{ width: "100%" }}
                className="rounded-2 p-3 w-full min-h-32 outline-hidden"
                id="content"
                name="content"
              >
                {(v) => (
                  <>
                    <span>{v}</span>
                    <span className="text-grey-5">{v ? " " : ""}</span>
                  </>
                )}
              </TextArea>
            </div>
          </div>
          <div className="flex gap-2 mt-2 justify-center items-center">
            <Button
              type="submit"
              color="gradient"
              size="m"
              className="min-w-72"
              leftIcon="sparkle"
              isDisabled={!value}
            >
              <FormattedMessage id="ai_reveals.generate_preview_cta" />
            </Button>
          </div>
        </Form>
      )}
    </ModalDialog>
  );
}
